/**
 * PATH: packages/email-hub/src/feature/components/LinkToJobModal.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-LINKTOJOBMODAL-001
 * PURPOSE: Canonical body of this email component. Lifted from
 *          apps/<tenant>/src/features/email/components/LinkToJobModal.tsx
 *          on K3a (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale (BUG-0032 was
 *          the most recent example). One canonical body, both
 *          tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/components/LinkToJobModal.tsx:
 *     thin re-export shim that points back here.
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { Briefcase, Link2, Loader2, MapPin, Search, Sparkles, X } from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import { allTokensMatchFields, tokenizeSearchQuery } from '@quotech/ui'
import { useQuery } from '@tanstack/react-query'
import api from '../../_internal/hooks/useApi'
import {
  useEmailJobLink,
  useEmailJobSuggestions,
  type EmailAttachment,
} from '../hooks/useEmails'

interface LinkToJobModalProps {
  emailId: string
  emailSubject: string
  emailBody?: string
  emailFrom?: string
  isOpen: boolean
  onClose: () => void
  onLinked?: () => void
}

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((t) => t.length > 2)
}

function scoreJobMatch(job: any, subjectTokens: string[], bodyTokens: string[], fromAddr: string): number {
  let score = 0
  const jobNum = (job.job_number || '').toLowerCase()
  const client = (job.client_name || '').toLowerCase()
  const address = (job.site_address || '').toLowerCase()
  const scope = (job.scope || '').toLowerCase()
  const allText = `${jobNum} ${client} ${address} ${scope}`

  const allEmailTokens = [...subjectTokens, ...bodyTokens]
  const subjectStr = subjectTokens.join(' ')

  if (jobNum && subjectStr.includes(jobNum)) score += 50
  if (jobNum && bodyTokens.join(' ').includes(jobNum)) score += 30

  const addressParts = address.split(/[\s,]+/).filter((p: string) => p.length > 2)
  for (const part of addressParts) {
    if (subjectTokens.includes(part)) score += 15
    if (bodyTokens.includes(part)) score += 8
  }

  const clientParts = client.split(/[\s,]+/).filter((p: string) => p.length > 2)
  for (const part of clientParts) {
    if (subjectTokens.includes(part)) score += 12
    if (bodyTokens.includes(part)) score += 6
  }

  if (fromAddr && client) {
    const fromLower = fromAddr.toLowerCase()
    for (const part of clientParts) {
      if (fromLower.includes(part)) score += 10
    }
  }

  for (const token of allEmailTokens) {
    if (allText.includes(token)) score += 1
  }

  return score
}

export default function LinkToJobModal({ emailId, emailSubject, emailBody, emailFrom, isOpen, onClose, onLinked }: LinkToJobModalProps) {
  const [search, setSearch] = useState('')
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null)
  const [linkType, setLinkType] = useState('general')
  const { linkToJob, isLinking } = useEmailJobLink()

  const { data: jobs = [] } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => api.get('/jobs').then((r) => r.data),
    enabled: isOpen,
  })

  // Backend-scored suggestions: ports the same scoreJobMatch logic but
  // runs server-side over the *stored* email body (not the truncated
  // preview the modal receives), and applies the AUTO_LINK_THRESHOLD
  // so we can pre-select the top match when the score is strong
  // enough that the auto-allocator would have written it at sync time.
  const { data: suggestions } = useEmailJobSuggestions(
    isOpen ? emailId : null,
    isOpen,
  )

  const subjectTokens = useMemo(() => tokenize(emailSubject || ''), [emailSubject])
  const bodyTokens = useMemo(() => tokenize((emailBody || '').slice(0, 2000)), [emailBody])

  const filtered = useMemo(() => {
    let results: any[]

    // W4a — canonical AND-of-tokens matching (shared with the Cmd+K
    // palette). Field selection is unchanged.
    const queryTokens = tokenizeSearchQuery(search)
    if (queryTokens.length > 0) {
      results = jobs.filter((j: any) =>
        allTokensMatchFields(
          [j.job_number, j.client_name, j.site_address, j.scope],
          queryTokens,
        ),
      )
    } else {
      results = [...jobs]
    }

    const scored = results.map((j: any) => ({
      job: j,
      score: scoreJobMatch(j, subjectTokens, bodyTokens, emailFrom || ''),
    }))

    scored.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score
      return String(b.job.job_number).localeCompare(String(a.job.job_number), undefined, { numeric: true })
    })

    return scored.slice(0, 50)
  }, [jobs, search, subjectTokens, bodyTokens, emailFrom])

  useEffect(() => {
    if (!isOpen) {
      setSearch('')
      setSelectedJobId(null)
      setLinkType('general')
    }
  }, [isOpen])

  // Auto-preselect the backend's top suggestion when it clears the
  // auto-link threshold, so the user can hit "Link to Job" in a
  // single keystroke. Skipped if the user has already manually
  // chosen a job (don't stomp on an explicit click).
  useEffect(() => {
    if (!isOpen || !suggestions || selectedJobId) return
    const top = suggestions.suggestions?.[0]
    if (top && top.is_best) {
      setSelectedJobId(top.job_id)
    }
  }, [isOpen, suggestions, selectedJobId])

  const [attachProgress, setAttachProgress] = useState('')

  const uploadAttachmentsToJob = useCallback(async (jobId: number, attachments: EmailAttachment[]) => {
    const downloadable = attachments.filter(a => a.download_url && a.filename)
    if (downloadable.length === 0) return

    const { emailApiFetch } = await import('../lib/api')

    for (let i = 0; i < downloadable.length; i++) {
      const att = downloadable[i]
      setAttachProgress(`Uploading ${i + 1}/${downloadable.length}: ${att.filename}`)
      try {
        const resp = await emailApiFetch(att.download_url!, { method: 'GET' })
        if (!resp.ok) continue
        const blob = await resp.blob()
        const fd = new FormData()
        fd.append('file', blob, att.filename)
        fd.append('folder_path', 'emails')
        fd.append('category', 'email-attachment')
        await api.post(`/jobs/${jobId}/files/upload`, fd)
      } catch {
        // best-effort — skip failures
      }
    }
    setAttachProgress('')
  }, [])

  const handleLink = async () => {
    if (!selectedJobId) return
    const result = await linkToJob(emailId, selectedJobId, linkType)
    if (result) {
      try {
        const { emailApi } = await import('../lib/api')
        const fullEmail = await emailApi<any>(`/api/emails/${emailId}/`)
        if (fullEmail?.attachments?.length) {
          await uploadAttachmentsToJob(selectedJobId, fullEmail.attachments)
        }
      } catch {
        // attachments are best-effort; the link itself succeeded
      }
      onLinked?.()
      onClose()
    }
  }

  if (!isOpen) return null

  const topScore = filtered[0]?.score ?? 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-xl shadow-2xl w-[560px] max-h-[640px] flex flex-col animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-primary" />
            <h2 className="font-semibold text-foreground">Link Email to Job</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        <div className="px-4 pt-3 pb-2">
          <p className="text-xs text-muted-foreground mb-3 truncate">
            {emailSubject}
          </p>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search jobs by number, client, address, or scope..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary focus:outline-none"
            />
          </div>

          <div className="flex items-center gap-2 mb-2">
            <label className="text-xs text-muted-foreground">Type:</label>
            {['general', 'approval', 'query', 'certification'].map((t) => (
              <button
                key={t}
                onClick={() => setLinkType(t)}
                className={cn(
                  'px-2 py-0.5 text-xs rounded-full border transition-colors capitalize',
                  linkType === t
                    ? 'bg-primary/10 border-primary/30 text-primary'
                    : 'border-border text-muted-foreground hover:bg-accent'
                )}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Backend "Top suggestions" pill row — surfaces the
              highest-scoring jobs from the server's matcher so the
              user doesn't have to scroll the full list to find the
              obvious pick. Only shown when not actively searching. */}
          {!search.trim() && suggestions?.suggestions?.length ? (
            <div className="mb-1">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center gap-1">
                <Sparkles className="h-2.5 w-2.5 text-primary" />
                Top suggestions
              </p>
              <div className="flex flex-wrap gap-1.5">
                {suggestions.suggestions.slice(0, 4).map((s) => {
                  const isSel = selectedJobId === s.job_id
                  return (
                    <button
                      key={s.job_id}
                      onClick={() => setSelectedJobId(s.job_id)}
                      title={`Score ${s.score}${s.is_best ? ' - Best match' : ''}`}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-medium border transition-colors max-w-[260px]',
                        isSel
                          ? 'bg-primary/10 border-primary/40 text-primary'
                          : s.is_best
                          ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-400/40 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-950/30'
                          : 'bg-background border-border text-foreground hover:bg-accent',
                      )}
                    >
                      <Briefcase className="h-3 w-3 shrink-0" />
                      <span className="truncate">
                        #{s.job_number}
                        {s.client_name && ` - ${s.client_name}`}
                      </span>
                      {s.is_best && (
                        <Sparkles className="h-2.5 w-2.5 shrink-0" />
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-2 min-h-0">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No jobs found</p>
          ) : (
            <div className="space-y-1">
              {filtered.map(({ job, score }: { job: any; score: number }) => (
                <button
                  key={job.id}
                  onClick={() => setSelectedJobId(job.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors',
                    selectedJobId === job.id
                      ? 'bg-primary/10 border border-primary/30'
                      : 'hover:bg-accent border border-transparent'
                  )}
                >
                  <Briefcase className={cn('h-4 w-4 shrink-0 mt-0.5', selectedJobId === job.id ? 'text-primary' : 'text-muted-foreground')} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-foreground">{job.job_number}</span>
                      {job.client_name && (
                        <span className="text-xs text-muted-foreground truncate">{job.client_name}</span>
                      )}
                      {score > 0 && topScore > 0 && score === topScore && (
                        <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary shrink-0">
                          <Sparkles className="h-2.5 w-2.5" />Best match
                        </span>
                      )}
                    </div>
                    {job.site_address && (
                      <p className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                        <MapPin className="h-3 w-3 shrink-0" />
                        <span className="truncate">{job.site_address}</span>
                      </p>
                    )}
                    {job.scope && (
                      <p className="text-[11px] text-muted-foreground/70 truncate mt-0.5">{job.scope}</p>
                    )}
                  </div>
                  {job.status && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0">
                      {job.status}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-accent transition-colors">
            Cancel
          </button>
          <button
            onClick={handleLink}
            disabled={!selectedJobId || isLinking || !!attachProgress}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {(isLinking || attachProgress) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            {attachProgress || (isLinking ? 'Linking...' : 'Link to Job')}
          </button>
        </div>
      </div>
    </div>
  )
}
